import { Immutable } from "immer";
import React from "react";

import { ISafeHTML, WebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
} from "../../../models/product-compare";
import { useViewport } from "../../../store/common/hooks";
import { useAppSelector } from "../../../store/reducers";
import { trackCategoryPageView } from "../../../utils/analytics";
import { notEmpty } from "../../../utils/functional";
// eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
import { rootProductSelector } from "../../configurator/selectors";
import { defaults } from "../defaults";
import { getSelectedVariant } from "../selectors";
import { ProductCompareGrid__ColumnCard_Component } from "./ProductCompareGrid__ColumnCardContent";

type IProps = Immutable<{
    header: ISafeHTML;
    basketLink: WebPageURL;
    financingLink: WebPageURL;
    preselectedSize: string | null;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    tiles: readonly ICompareTile[];
    returnUrl: string;
}>;

const trackCategoryViewForTiles = (
    tiles: readonly ICompareTile[],
    priceSelectValue: string,
): void => {
    if (!tiles) {
        return;
    }
    // Obtain SKUs for current variants
    const skus = tiles
        .map((tile) => {
            const variant = getSelectedVariant(tile, priceSelectValue);
            return variant ? variant.skus[0] : null;
        })
        .filter(notEmpty);
    trackCategoryPageView(skus);
};

export const ProductCompareGrid__ColumnCard = (
    props: IProps,
): React.ReactNode => {
    const hasMountedRef = React.useRef(false);
    const priceSelectValue = useAppSelector(
        (state) => (state.productcompare || defaults).priceSelectValue,
    );
    const viewport = useViewport();
    const pageRootProduct = useAppSelector((state) => {
        const rootProduct = rootProductSelector(state.configurator);
        return rootProduct ? rootProduct.id : null;
    });

    React.useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        trackCategoryViewForTiles(props.tiles, priceSelectValue);
    }, [props.tiles, priceSelectValue]);

    return (
        <ProductCompareGrid__ColumnCard_Component
            header={props.header}
            tiles={props.tiles}
            basketLink={props.basketLink}
            financingLink={props.financingLink}
            sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
            preselectedSize={props.preselectedSize || null}
            priceSelectValue={priceSelectValue}
            pageRootProduct={pageRootProduct}
            returnUrl={props.returnUrl}
            viewport={viewport}
        />
    );
};
